import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Container, Typography, Grid, FormControl, InputLabel, Select,
  MenuItem, CircularProgress, Paper, Alert, Tooltip as MuiTooltip, IconButton, Card,
  CardContent, FormGroup, FormControlLabel, Checkbox
} from '@mui/material';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import supabase from '../../../utils/supabase';

const RANK_COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F',
  '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57', '#ff7300', '#413ea0'
];

const calculateLinearRegression = (data) => {
  if (!data || data.length < 2) {
    return { slope: null, intercept: null, rSquared: null };
  }
  const n = data.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  const points = [];

  data.forEach(point => {
    const x = point.originalYear || point.year;
    const y = Object.keys(point).reduce((total, key) => (key !== 'year' && key !== 'originalYear' && key !== 'trend' && key !== 'ma5' && key !== 'ma10' && key !== 'growthRate') ? total + point[key] : total, 0);
    points.push({ x, y });

    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  const numerator = n * sumXY - sumX * sumY;
  const denominator = n * sumX2 - sumX * sumX;

  if (denominator === 0) {
    return { slope: null, intercept: null, rSquared: null };
  }

  const slope = numerator / denominator;
  const intercept = (sumY / n) - slope * (sumX / n);
  const meanY = sumY / n;

  let ssTot = 0;
  let ssRes = 0;

  points.forEach(point => {
    const predictedY = slope * point.x + intercept;
    ssTot += Math.pow(point.y - meanY, 2);
    ssRes += Math.pow(point.y - predictedY, 2);
  });

  const rSquared = ssTot === 0 ? 1 : 1 - (ssRes / ssTot);

  return { slope, intercept, rSquared };
};

const AnnualDescriptionTrends = () => {
  const { t } = useTranslation(['analysis', 'common']);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [rankFilter, setRankFilter] = useState('All');
  const [subfamilyFilter, setSubfamilyFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  const [rankOptions, setRankOptions] = useState([]);
  const [subfamilyOptions, setSubfamilyOptions] = useState([]);
  const [fullYearRange, setFullYearRange] = useState([0, 0]);
  const [startYear, setStartYear] = useState(0);
  const [endYear, setEndYear] = useState(0);

  const [showMA5, setShowMA5] = useState(false);
  const [showMA10, setShowMA10] = useState(false);
  const [yearGrouping, setYearGrouping] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allData = [];
      let page = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error: rpcError } = await supabase.rpc('get_yearly_taxa_stats', { p_limit: pageSize, p_offset: page * pageSize });
        if (rpcError) throw rpcError;
        if (data && data.length > 0) {
          allData.push(...data);
          if (data.length < pageSize) hasMore = false;
          else page++;
        } else {
          hasMore = false;
        }
      }
      setRawData(allData);
      const ranks = [...new Set(allData.map(item => item.rank))].sort();
      const subfamilies = [...new Set(allData.map(item => item.subfamily_name))].filter(Boolean).sort();
      setRankOptions(['All', ...ranks]);
      setSubfamilyOptions(['All', ...subfamilies]);
    } catch (e) {
      console.error("Failed to fetch taxa trends:", e);
      setError(t('error.fetchMessage'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const titleSx = {
    fontWeight: 800,
    letterSpacing: "0.01em",
    color: "text.primary",
    textAlign: "center",
    mb: 1,
  };

  const subtitleSx = {
    textAlign: "center",
    whiteSpace: "pre-line",
    color: "text.secondary",
    maxWidth: 820,
    mx: "auto",
    mb: 4,
  };

  useEffect(() => {
    if (rawData.length > 0 && fullYearRange[0] === 0) {
      const years = rawData.map(item => item.authority_year).filter(Boolean);
      if (years.length > 0) {
        const min = Math.min(...years);
        const max = Math.max(...years);
        setFullYearRange([min, max]);
        setStartYear(min);
        setEndYear(max);
      }
    }
  }, [rawData, fullYearRange]);

  const yearOptions = useMemo(() => {
    if (fullYearRange[0] === 0) return [];
    const options = [];
    for (let y = fullYearRange[0]; y <= fullYearRange[1]; y++) {
      options.push(y);
    }
    return options;
  }, [fullYearRange]);

  const handleStartYearChange = (event) => {
    const newStartYear = event.target.value;
    setStartYear(newStartYear);
    if (newStartYear > endYear) setEndYear(newStartYear);
  };

  const handleEndYearChange = (event) => {
    const newEndYear = event.target.value;
    setEndYear(newEndYear);
    if (newEndYear < startYear) setStartYear(newEndYear);
  };

  const { chartData, allRanks, totalCount, trendStats } = useMemo(() => {
    const filteredData = rawData.filter(item => {
      const rankMatch = rankFilter === 'All' || item.rank === rankFilter;
      const subfamilyMatch = subfamilyFilter === 'All' || item.subfamily_name === subfamilyFilter;
      const statusMatch = statusFilter === 'All' || (statusFilter === 'Valid' && item.status === 'valid') || (statusFilter === 'Invalid' && item.status !== 'valid');
      const yearMatch = !item.authority_year || (item.authority_year >= startYear && item.authority_year <= endYear);
      return rankMatch && subfamilyMatch && statusMatch && yearMatch;
    });

    if (filteredData.length === 0 || startYear === 0) {
      return { chartData: [], allRanks: [], totalCount: 0, trendStats: {} };
    }

    const totalCount = filteredData.reduce((acc, item) => acc + item.count, 0);
    const ranks = [...new Set(filteredData.map(item => item.rank))].sort();

    const yearlyCountsByRank = filteredData.reduce((acc, item) => {
      const { authority_year: year, rank, count } = item;
      if (year) {
        if (!acc[year]) acc[year] = {};
        acc[year][rank] = (acc[year][rank] || 0) + count;
      }
      return acc;
    }, {});

    let chartData = [];
    for (let year = startYear; year <= endYear; year++) {
      const yearData = { year };
      const countsForYear = yearlyCountsByRank[year] || {};
      ranks.forEach(rank => {
        yearData[rank] = countsForYear[rank] || 0;
      });
      chartData.push(yearData);
    }

    if (yearGrouping > 1) {
      const groupedData = [];
      for (let i = 0; i < chartData.length; i += yearGrouping) {
        const chunk = chartData.slice(i, i + yearGrouping);
        const firstYear = chunk[0].year;
        const lastYear = chunk[chunk.length - 1].year;
        const yearLabel = firstYear === lastYear ? `${firstYear}` : `${firstYear}-${lastYear}`;
        const aggregated = { year: yearLabel, originalYear: firstYear };
        ranks.forEach(rank => {
          aggregated[rank] = chunk.reduce((sum, d) => sum + d[rank], 0);
        });
        groupedData.push(aggregated);
      }
      chartData = groupedData;
    }

    const movingAverageWindow5 = [];
    const movingAverageWindow10 = [];

    chartData.forEach((d, index) => {
      if (yearGrouping === 1) {
        const totalCountForYear = Object.keys(d).reduce((sum, key) => (key !== 'year' && key !== 'trend') ? sum + d[key] : sum, 0);
        if (index > 0) {
          const prevTotalCount = Object.keys(chartData[index - 1]).reduce((sum, key) => (key !== 'year' && key !== 'trend') ? sum + chartData[index - 1][key] : sum, 0);
          d.growthRate = prevTotalCount > 0 ? ((totalCountForYear - prevTotalCount) / prevTotalCount) * 100 : 0;
        } else {
          d.growthRate = 0;
        }
        movingAverageWindow5.push(totalCountForYear);
        if (movingAverageWindow5.length > 5) movingAverageWindow5.shift();
        d.ma5 = movingAverageWindow5.reduce((a, b) => a + b, 0) / movingAverageWindow5.length;
        movingAverageWindow10.push(totalCountForYear);
        if (movingAverageWindow10.length > 10) movingAverageWindow10.shift();
        d.ma10 = movingAverageWindow10.reduce((a, b) => a + b, 0) / movingAverageWindow10.length;
      }
    });

    const trendStats = calculateLinearRegression(chartData);

    if (trendStats.slope !== null) {
      const { slope, intercept } = trendStats;
      chartData.forEach(d => {
        d.trend = Math.max(0, slope * (d.originalYear || d.year) + intercept);
      });
    }

    return { chartData, allRanks: ranks, totalCount, trendStats };
  }, [rawData, rankFilter, subfamilyFilter, statusFilter, startYear, endYear, yearGrouping]);

  return (
    <Box p={{ xs: 2, md: 4 }}>
      <Container maxWidth="xl">
        <Typography variant="h2" sx={titleSx}>
          {t('annualTrends.title')}
        </Typography>
        <Typography variant="body1" sx={subtitleSx}>
          {t('annualTrends.description')}
        </Typography>

        {loading && <CircularProgress sx={{ display: 'block', margin: 'auto', my: 4 }} />}
        
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            <Typography variant="h6">{t('error.title')}</Typography>
            <Typography>{error}</Typography>
            <Typography sx={{ mt: 1 }}>
              <strong>{t('error.adminHintTitle')}</strong> {t('error.adminHintBody')}
            </Typography>
          </Alert>
        )}

        {!loading && !error && (
          <>
            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 3, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SettingsIcon sx={{ mr: 1 }} />
                <Typography variant="h6">{t('parametersTitle')}</Typography>
              </Box>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:subfamily')}</InputLabel><Select value={subfamilyFilter} label={t('common:subfamily')} onChange={(e) => setSubfamilyFilter(e.target.value)}>{subfamilyOptions.map(sf => <MenuItem key={sf} value={sf}>{sf}</MenuItem>)}</Select></FormControl></Grid>
                <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:rank')}</InputLabel><Select value={rankFilter} label={t('common:rank')} onChange={(e) => setRankFilter(e.target.value)}>{rankOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}</Select></FormControl></Grid>
                <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>{t('common:status')}</InputLabel><Select value={statusFilter} label={t('common:status')} onChange={(e) => setStatusFilter(e.target.value)}><MenuItem value="All">{t('common:all')}</MenuItem><MenuItem value="Valid">{t('common:valid')}</MenuItem><MenuItem value="Invalid">{t('common:invalid')}</MenuItem></Select></FormControl></Grid>
                <Grid item xs={6} sm={3} md={1.5}><FormControl fullWidth><InputLabel>{t('startYear')}</InputLabel><Select value={startYear} label={t('startYear')} onChange={handleStartYearChange} disabled={yearOptions.length === 0}>{yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}</Select></FormControl></Grid>
                <Grid item xs={6} sm={3} md={1.5}><FormControl fullWidth><InputLabel>{t('endYear')}</InputLabel><Select value={endYear} label={t('endYear')} onChange={handleEndYearChange} disabled={yearOptions.length === 0}>{yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}</Select></FormControl></Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: { xs: 1, sm: 2, md: 3 }, mb: 3, backdropFilter: 'blur(5px)', backgroundColor: 'rgba(30,30,30,0.5)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BarChartIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('annualTrends.annualChartTitle')}</Typography>
                <Typography variant="subtitle2" sx={{ mr: 2 }}>{t('annualTrends.annualViewOptions')}</Typography>
                <FormControl size="small" sx={{ mr: 2, minWidth: 120 }}><InputLabel>{t('annualTrends.groupByYears')}</InputLabel><Select value={yearGrouping} label={t('annualTrends.groupByYears')} onChange={(e) => setYearGrouping(e.target.value)}><MenuItem value={1}>1</MenuItem><MenuItem value={3}>3</MenuItem><MenuItem value={5}>5</MenuItem><MenuItem value={10}>10</MenuItem></Select></FormControl>
                <FormGroup row>
                  <FormControlLabel control={<Checkbox disabled={yearGrouping > 1} checked={showMA5} onChange={(e) => setShowMA5(e.target.checked)} />} label={t('annualTrends.showMA5')} />
                  <FormControlLabel control={<Checkbox disabled={yearGrouping > 1} checked={showMA10} onChange={(e) => setShowMA10(e.target.checked)} />} label={t('annualTrends.showMA10')} />
                </FormGroup>
              </Box>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={500}>
                  <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis yAxisId="left" label={{ value: t('taxaCount'), angle: -90, position: 'insideLeft' }} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(30, 30, 30, 0.95)', border: '1px solid rgba(127, 255, 212, 0.3)', borderRadius: '8px' }} formatter={(value, name) => name === t('annualTrends.growthRate') ? `${value.toFixed(2)}%` : value} />
                    <Legend />
                    <Line yAxisId="left" dataKey="growthRate" name={t('annualTrends.growthRate')} stroke="transparent" dot={false} activeDot={false} legendType="none" />
                    {allRanks.map((rank, index) => (<Bar yAxisId="left" key={rank} dataKey={rank} stackId="a" fill={RANK_COLORS[index % RANK_COLORS.length]} />))}
                    {showMA5 && <Line yAxisId="left" type="monotone" dataKey="ma5" name={t('annualTrends.showMA5')} stroke="#ffeb3b" strokeWidth={2} dot={false} />}
                    {showMA10 && <Line yAxisId="left" type="monotone" dataKey="ma10" name={t('annualTrends.showMA10')} stroke="#ff9800" strokeWidth={2} dot={false} />}
                    {trendStats.slope !== null && <Line yAxisId="left" type="monotone" dataKey="trend" name={t('trendLineName')} stroke="#ff4500" strokeWidth={2} dot={false} />}
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: 'transparent' }}><Typography color="text.secondary">{t('noData')}</Typography></Paper>
              )}
            </Paper>

            {chartData.length > 0 && (
              <Box>
                <Box mb={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}><AssessmentIcon sx={{ mr: 1 }} /><Typography variant="h6">{t('resultsTitle')}</Typography></Box>
                  <Card sx={{ backgroundColor: 'rgba(0,0,0,0.2)', display: 'inline-block', minWidth: 275 }}><CardContent sx={{ textAlign: 'center', p: 3 }}><Typography color="text.secondary" gutterBottom>{t('taxaNumberTitle')}</Typography><Typography variant="h4" component="div">{totalCount.toLocaleString()}</Typography></CardContent></Card>
                </Box>

                {trendStats.slope !== null && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}><AssessmentIcon sx={{ mr: 1 }} /><Typography variant="h6">{t('linearRegressionTitle')}</Typography></Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}><Card sx={{ backgroundColor: 'rgba(0,0,0,0.2)', height: '100%' }}><CardContent sx={{ textAlign: 'center', p: 3 }}><Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Typography color="text.secondary" gutterBottom>{t('trendSlope')}</Typography><MuiTooltip title={t('stats.trendTooltip')}><IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton></MuiTooltip></Box><Typography variant="h4" component="div">{trendStats.slope.toFixed(2)}</Typography></CardContent></Card></Grid>
                      <Grid item xs={12} sm={6} md={4}><Card sx={{ backgroundColor: 'rgba(0,0,0,0.2)', height: '100%' }}><CardContent sx={{ textAlign: 'center', p: 3 }}><Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Typography color="text.secondary" gutterBottom>{t('rSquared')}</Typography><MuiTooltip title={t('stats.rSquaredTooltip')}><IconButton size="small"><InfoOutlinedIcon fontSize="inherit" /></IconButton></MuiTooltip></Box><Typography variant="h4" component="div">{trendStats.rSquared.toFixed(3)}</Typography></CardContent></Card></Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default AnnualDescriptionTrends;
